import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import LearnLanguageNav from './LearnLanguageNav';
import {
  ARTICLE_BY_SLUG, ORIGIN, formatDate, homePath, learnArticlePath, learnHubPath,
  loadArticle, noteLearnClick, pageDir, readyLearnLangs, useLearnBundle,
} from './learnData';
import './LearnTarot.css';

const ENGLISH_UI = {
  home: 'Home',
  learn: 'Learn Tarot',
  inThisGuide: 'In this guide',
  tocLabel: 'Table of contents',
  quickAnswer: 'Quick answer',
  loading: 'Loading guide…',
  minRead: '{n} min read',
  tryIt: 'Try it yourself',
  sources: 'Sources & Further Reading',
  method: 'How this guide was made: we gathered published sources on the topic and checked the factual claims against them. Card meanings and reading methods are traditions, which readers interpret in different ways.',
  faq: 'Common questions',
  continue: 'Continue learning',
  tools: 'Related NowTarot tools',
  breadcrumb: 'Breadcrumb',
  updated: 'Updated',
};

function useArticle(slug) {
  const [state, setState] = useState({ slug: null, article: undefined });
  useEffect(() => {
    let alive = true;
    if (!slug) {
      setState({ slug: '', article: null });
      return undefined;
    }
    loadArticle(slug).then((article) => {
      if (alive) setState({ slug, article });
    });
    return () => { alive = false; };
  }, [slug]);
  if (!slug) return null;
  return state.slug === slug ? state.article : undefined;
}

function TableOfContents({ toc, label }) {
  if (!toc.length) return null;
  const list = (
    <ol>
      {toc.map((item) => (
        <li key={item.id}><a href={`#${item.id}`}>{item.text}</a></li>
      ))}
    </ol>
  );
  return (
    <>
      <details className="lt-toc lt-toc-mobile">
        <summary>{label}</summary>
        {list}
      </details>
      <nav className="lt-toc lt-toc-desktop" aria-label={label}>
        <p className="lt-toc-title">{label}</p>
        {list}
      </nav>
    </>
  );
}

function alternateLinks(slug) {
  const langs = readyLearnLangs();
  if (langs.length < 2) return null;
  return [
    <link key="x-default" rel="alternate" hrefLang="x-default" href={`${ORIGIN}${learnArticlePath(slug, 'en')}`} />,
    ...langs.map((item) => (
      <link
        key={item.code}
        rel="alternate"
        hrefLang={item.code}
        href={`${ORIGIN}${learnArticlePath(slug, item.code)}`}
      />
    )),
  ];
}

const LearnArticle = ({ lang = 'en', slug }) => {
  const navigate = useNavigate();
  const bundle = useLearnBundle(lang);
  const english = useArticle(lang === 'en' ? slug : null);
  const dir = pageDir(lang);

  if (lang === 'en' && !ARTICLE_BY_SLUG[slug]) return <Navigate to={learnHubPath('en')} replace />;
  if (lang !== 'en' && bundle === undefined) {
    return (
      <div className="learn-page" lang={lang} dir={dir}>
        <Header />
        <main className="lt-container"><p className="lt-loading">…</p></main>
        <Footer />
      </div>
    );
  }
  if (lang !== 'en' && !bundle) return <Navigate to={learnHubPath('en')} replace />;

  const article = lang === 'en' ? english : bundle.articles[slug];
  if (lang !== 'en' && article === undefined) return <Navigate to={learnHubPath(lang)} replace />;
  const meta = lang === 'en' ? ARTICLE_BY_SLUG[slug] : article;
  if (!meta) return <Navigate to={learnHubPath(lang)} replace />;

  const ui = lang === 'en' ? ENGLISH_UI : bundle.hub.ui;
  const canonical = `${ORIGIN}${learnArticlePath(slug, lang)}`;
  const hub = learnHubPath(lang);
  const home = homePath(lang);
  const related = (article?.related || []).map((item) => {
    if (lang === 'en') return ARTICLE_BY_SLUG[item];
    return bundle.articles[item];
  }).filter(Boolean).slice(0, 4);

  const onBodyClick = (event) => {
    const anchor = event.target.closest('a');
    if (!anchor) return;
    const href = anchor.getAttribute('href') || '';
    if (anchor.dataset.learnLink === 'internal' && href.startsWith('/')) {
      noteLearnClick(href.startsWith('/learn-tarot/') ? 'learn_article_click' : 'learn_tool_click', href, slug);
      if (!event.metaKey && !event.ctrlKey && !event.shiftKey && event.button === 0) {
        event.preventDefault();
        navigate(href);
      }
    }
  };

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: meta.title,
      description: meta.metaDescription,
      ...(meta.featured?.src ? { image: `${ORIGIN}${meta.featured.src}` } : {}),
      datePublished: meta.published,
      dateModified: meta.updated,
      inLanguage: lang,
      mainEntityOfPage: canonical,
      author: { '@type': 'Organization', name: 'NowTarot', url: `${ORIGIN}/` },
      publisher: { '@type': 'Organization', name: 'NowTarot', url: `${ORIGIN}/`, logo: { '@type': 'ImageObject', url: `${ORIGIN}/logo512.png` } },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: ui.home, item: `${ORIGIN}${home}` },
        { '@type': 'ListItem', position: 2, name: ui.learn, item: `${ORIGIN}${hub}` },
        { '@type': 'ListItem', position: 3, name: meta.title, item: canonical },
      ],
    },
  ];

  return (
    <div className="learn-page" lang={lang} dir={dir}>
      <Helmet htmlAttributes={{ lang, dir }}>
        <title>{article?.metaTitle || `${meta.title} | NowTarot`}</title>
        <meta name="description" content={meta.metaDescription} />
        <link rel="canonical" href={canonical} />
        {alternateLinks(slug)}
        <meta property="og:title" content={article?.metaTitle || meta.title} />
        <meta property="og:description" content={meta.metaDescription} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={`${ORIGIN}/og-image.png`} />
        <meta property="article:published_time" content={meta.published} />
        <meta property="article:modified_time" content={meta.updated} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
      <Header />

      <main className="lt-container">
        <nav className="lt-breadcrumbs" aria-label={ui.breadcrumb}>
          <Link to={home}>{ui.home}</Link>
          <span aria-hidden="true"> › </span>
          <Link to={hub}>{ui.learn}</Link>
          <span aria-hidden="true"> › </span>
          <span>{meta.title}</span>
        </nav>

        <header className="lt-article-header">
          <p className="lt-chip">{meta.categoryLabel}</p>
          <h1>{meta.title}</h1>
          {meta.dek && <p className="lt-dek">{meta.dek}</p>}
          <p className="lt-article-meta">
            {(lang === 'en' ? ui.minRead : bundle.hub.minRead).replace('{n}', meta.readingMinutes)}
            {' · '}
            {ui.updated} <time dateTime={meta.updated}>{formatDate(meta.updated, lang)}</time>
          </p>
        </header>

        {meta.featured?.src && (
          <figure className="lt-hero-figure">
            <img src={meta.featured.src} alt={meta.featured.alt} width="1200" height="630" />
          </figure>
        )}

        {article === undefined && <p className="lt-loading">{ui.loading}</p>}

        {article && (
          <>
            <aside className="lt-quick-answer" aria-label={ui.quickAnswer}>
              <p className="lt-quick-answer-label">{ui.quickAnswer}</p>
              <p>{article.quickAnswer}</p>
            </aside>

            <div className="lt-article-layout">
              <TableOfContents toc={article.toc} label={ui.inThisGuide} />

              <div className="lt-article-main">
                {/* Article HTML is generated at build time from our own reviewed Markdown. */}
                {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                <article
                  className="lt-prose"
                  onClick={onBodyClick}
                  dangerouslySetInnerHTML={{ __html: article.html }}
                />

                <aside className="lt-try-it" aria-label={ui.tryIt}>
                  <p className="lt-try-it-label">{ui.tryIt}</p>
                  <p>{article.tryIt.text}</p>
                  <Link
                    to={article.tryIt.url}
                    className="lt-button"
                    onClick={() => noteLearnClick('learn_tool_click', article.tryIt.url, slug)}
                  >
                    {article.tryIt.label} <span className="lt-arrow" aria-hidden="true">→</span>
                  </Link>
                </aside>

                {article.sources.length > 0 && (
                  <details className="lt-sources-panel">
                    <summary>{ui.sources}({article.sources.length})</summary>
                    <ol className="lt-sources">
                      {article.sources.map((source) => (
                        <li key={source.n} id={`source-${source.n}`}>
                          <a href={source.url} target="_blank" rel="noopener noreferrer">{source.title}</a>
                          {source.publisher && <span> — {source.publisher}</span>}
                          {source.published && <span> ({source.published})</span>}
                        </li>
                      ))}
                    </ol>
                    <p className="lt-method">{ui.method}</p>
                  </details>
                )}

                {article.faq.length > 0 && (
                  <section className="lt-faq" aria-labelledby="lt-faq-title">
                    <h2 id="lt-faq-title">{ui.faq}</h2>
                    {article.faq.map((item) => (
                      <details key={item.q}>
                        <summary>{item.q}</summary>
                        <p>{item.a}</p>
                      </details>
                    ))}
                  </section>
                )}

                {related.length > 0 && (
                  <section className="lt-continue" aria-labelledby="lt-continue-title">
                    <h2 id="lt-continue-title">{ui.continue}</h2>
                    <div className="lt-continue-grid">
                      {related.map((item) => (
                        <Link
                          to={item.path}
                          key={item.slug}
                          className="lt-continue-card"
                          onClick={() => noteLearnClick('learn_article_click', item.path, slug)}
                        >
                          {item.featured?.src && <img src={item.featured.src} alt="" width="1200" height="630" loading="lazy" />}
                          <span>{item.title}</span>
                        </Link>
                      ))}
                    </div>
                  </section>
                )}

                <section className="lt-tools" aria-labelledby="lt-tools-title">
                  <h2 id="lt-tools-title">{ui.tools}</h2>
                  <ul>
                    {article.tools.map((tool) => (
                      <li key={tool.to}>
                        <Link to={tool.to} onClick={() => noteLearnClick('learn_tool_click', tool.to, slug)}>
                          {tool.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </section>
              </div>
            </div>
            <LearnLanguageNav lang={lang} slug={slug} />
          </>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default LearnArticle;
